import cloudIcon from "@assets/images/cloud.svg";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { makeStyles } from "@saleor/macaw-ui";
import lodash_orderBy from "lodash/orderBy";
import React from "react";

interface DialogConfirmErrorProps {
  onCancel: () => void;
  onConfirm: () => void;
  open: boolean;
  errorSource?: string;
  errorOrders?: any[];
  errorItems?: any[];
  testId?: string;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      "& .MuiPaper-root": {
        maxWidth: 560,
        width: "100%",
        borderRadius: 16,
        maxHeight: "unset"
      },
      "& .MuiDialogTitle-root": {
        padding: 24
      },
      "& .MuiDialogContent-root": {
        paddingTop: 5
      },
      "& .MuiDialogActions-root": {
        borderTop: 0,
        paddingBottom: 24,
        columnGap: 16
      },
      "& .MuiButton-root": {
        fontSize: "1.8rem",
        "& .MuiTypography-root": {
          fontSize: "1.8rem"
        }
      }
    },
    dialogHeader: {
      padding: "4rem 24px 2rem",
      textAlign: "center"
    },
    cloudIcon: {
      width: 56,
      height: 56,
      marginBottom: "10px"
    },
    title: {
      fontSize: "22px",
      color: "#333",
      marginBottom: "5px"
    },
    subtitle: {
      fontSize: "18px",
      fontWeight: "normal",
      color: "#333"
    },
    dialogBody: {
      padding: "0 24px"
    },
    errorSource: {
      fontSize: "18px",
      fontWeight: "bold",
      color: theme.palette.error.light
    },
    errorText: {
      fontSize: "16px",
      color: "#333"
    },
    errorTableWrap: {
      border: "1px solid #ececec",
      margin: "2rem auto",
      borderRadius: "4px",
      maxHeight: "393px",
      overflowY: "auto"
    },
    errorTable: {
      "& .MuiTableCell-root": {
        paddingLeft: "16px !important",
        paddingRight: "16px !important",
        fontFamily: "ScgFont, sans-serif"
      },
      "& .MuiTableHead-root .MuiTableCell-head": {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#333",
        backgroundColor: "#fff2f6"
      },
      "& .MuiTableCell-root.MuiTableCell-head:first-child": {
        minWidth: "100px",
        textAlign: "center"
      },
      "& .MuiTableCell-root.MuiTableCell-head:nth-child(2)": {
        minWidth: "160px"
      },
      "& .MuiTableCell-body": {
        color: "#333"
      },
      "& .MuiTableCell-body:first-child": {
        textAlign: "center"
      },
      "& .MuiTableBody-root .MuiTableRow-root:not(.MuiTableBody-root .MuiTableRow-root:last-child) .MuiTableCell-root": {
        borderBottom: "1px solid #ececec"
      },
      "& .MuiTableRow-root:nth-child(even)": {
        backgroundColor: "white"
      },
      "& .MuiTableRow-root:hover:not(.MuiTableRow-head):not(.MuiTableRow-footer)": {
        backgroundColor: "white !important"
      },
      "& .MuiTableRow-root:hover:not(.MuiTableRow-head):not(.MuiTableRow-footer)::before": {
        width: "0"
      }
    },
    btnAction: {
      color: "white",
      background:
        "linear-gradient(90deg, #601863 0%, #681661 12%, #81125C 32%, #A90C55 57%, #DF044A 86%, #FD0045 100%) !important",
      borderRadius: "4px",
      width: "100%",
      padding: "12px",
      "&:hover": {
        background:
          "linear-gradient(90deg, #601863 0%, #681661 12%, #81125C 32%, #A90C55 57%, #DF044A 86%, #FD0045 100%) !important"
      }
    }
  }),
  { name: "DialogConfirmDelete" }
);

const DialogConfirmError: React.FC<DialogConfirmErrorProps> = ({
  onCancel,
  onConfirm,
  open,
  errorSource,
  errorOrders,
  errorItems,
  testId
}) => {
  const classes = useStyles({});

  const sortingErrorItem =
    errorItems && errorItems?.length > 0
      ? lodash_orderBy(
          errorItems?.map(err => ({
            ...err,
            itemNo: parseInt(err.itemNo, 10)
          })),
          ["itemNo"],
          ["asc"]
        )
      : [];

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      className={classes.root}
      data-testid={testId ?? "dialogConfirmError"}
    >
      <Box className={classes.dialogHeader}>
        <img src={cloudIcon} alt="" className={classes.cloudIcon} />
        <MyTypography variant="h6" className={classes.title}>
          ไม่สามารถทำรายการได้
        </MyTypography>
        <MyTypography variant="h6" className={classes.subtitle}>
          เนื่องจากเกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง
        </MyTypography>
      </Box>

      <Box className={classes.dialogBody}>
        <MyTypography variant="body3" className={classes.errorSource}>
          {errorSource ? errorSource : ""}
        </MyTypography>
        {errorOrders && errorOrders?.length > 0
          ? errorOrders.map(err => (
              <MyTypography
                variant="body3"
                className={classes.errorText}
                key={err + errorOrders?.length}
              >
                {err}
              </MyTypography>
            ))
          : null}

        {sortingErrorItem?.length > 0 ? (
          <Box className={classes.errorTableWrap}>
            <Table className={classes.errorTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Item No.</TableCell>
                  <TableCell>Error Code</TableCell>
                  <TableCell>Error Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortingErrorItem?.map(err => (
                  <TableRow key={err?.itemNo + errorOrders?.length}>
                    <TableCell>{err?.itemNo}</TableCell>
                    <TableCell>{err?.itemCode}</TableCell>
                    <TableCell>{err?.itemStatus}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : null}
      </Box>

      <DialogActions disableSpacing={true}>
        <Button
          id="closeButton"
          color="primary"
          variant="contained"
          className={classes.btnAction}
          fullWidth={true}
          onClick={onConfirm}
        >
          <MyTypography variant="body1">ปิดหน้าต่าง</MyTypography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogConfirmError;
